import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
    MdIconSearch,
    MdIconClose,
} from '@antiqua-dls/vue-components';
import agGrid from 'src/components/agGrid/index.vue';
import i18n from 'src/assets/i18n/i18n';

// Import custom column components
import DisplayStyleNameCol from '../components/displayStyleNameCol.vue';
import StatusCol from '../components/statusCol.vue';
import PriceCol from '../components/priceCol.vue';
import ContractNumbersCol from '../components/contractNumbersCol.vue';

import { getMaterialNumberForBinary } from 'src/utils/helper/misc';

export default {
    name: 'FontDisplayStylesTable',
    components: {
        agGrid,
        MdIconSearch,
        MdIconClose,
        DisplayStyleNameCol,
        StatusCol,
        PriceCol,
        ContractNumbersCol,
    },
    props: {
        data: {
            type: Array,
            default: () => []
        },
        isLiveIngestion: {
            type: Boolean,
            default: false
        }
    },
    setup(props) {
        const resizeObserver = new ResizeObserver(resizeGridToAdjust);
        const $t = i18n().global.t;
        const gridApi = ref({})
        const rowData = ref([])
        const tableContainerRef = ref(null);

        const colDefs = computed(() => {
            const columns = [
                {
                    headerName: $t('pages.inventory_ingestion_hub.font_display_styles_table.display_style_name'),
                    field: 'displayStyleName',
                    suppressMovable: true,
                    wrapHeaderText: true,
                    flex: 2,
                    resizable: false,
                    cellRenderer: DisplayStyleNameCol,
                    cellDataType: false,
                    sortable: false,
                },
                {
                    headerName: $t('pages.inventory_ingestion_hub.font_display_styles_table.status'),
                    field: 'status',
                    suppressMovable: true,
                    flex: 1,
                    resizable: false,
                    cellRenderer: StatusCol,
                    cellDataType: false,
                    sortable: false,
                },
                {
                    headerName: $t('pages.inventory_ingestion_hub.font_display_styles_table.price'),
                    field: 'price',
                    suppressMovable: true,
                    flex: 0.8,
                    resizable: false,
                    cellRenderer: PriceCol,
                    cellDataType: false,
                    sortable: false,
                },
            ];

            if (props.isLiveIngestion) {
                columns.push({
                    headerName: $t('pages.inventory_ingestion_hub.font_display_styles_table.material_number'),
                    field: 'materialNumber',
                    suppressMovable: true,
                    flex: 1.5,
                    resizable: false,
                    cellDataType: false,
                    sortable: false,
                });
            }

            columns.push({
                headerName: $t('pages.inventory_ingestion_hub.font_display_styles_table.contract_numbers'),
                field: 'contractNumbers',
                suppressMovable: true,
                flex: 1.5,
                resizable: false,
                cellRenderer: ContractNumbersCol,
                cellDataType: false,
                sortable: false,
            });

            return columns;
        });

        const capitalizeFirstLetter = (value) => {
            const str = value != null ? String(value).trim() : '';
            if (str === '') return str;
            return str.charAt(0).toUpperCase() + str.slice(1);
        };


        const updateRowData = (submission) => {
            const rowDataValue = [];
            if (submission.revisionDetails?.userMetaData?.binaries) {
                submission.revisionDetails.userMetaData.binaries.forEach(binary => {
                    const materialNumber = getMaterialNumberForBinary(
                        binary,
                        submission.revisionDetails?.userMetaData?.packages
                    );
                    rowDataValue.push({
                        id: binary.binaryId,
                        displayStyleName: {
                            name: binary.styleName || '—',
                            version: binary.fontInfo?.versionText || '—',
                            fileFormat: binary.fontInfo?.fontFormat || '—',
                            formatFlavor: binary.fontInfo?.formatFlavor || '—',
                        },
                        status: (binary?.status != null && String(binary.status).trim() !== '') ? capitalizeFirstLetter(binary.status) : (submission?.isEdit ? 'Existing' : 'New'),
                        price: binary.prices?.[0]?.price || '—',
                        materialNumber: materialNumber || '—',
                        contractNumbers: binary.contractNumber || ['—'],
                    });
                });
            }
            rowData.value = rowDataValue;
        }



        // Function to resize the grid
        function resizeGridToAdjust() {
            if (gridApi?.value?.sizeColumnsToFit) {
                gridApi.value.sizeColumnsToFit();
            }
        }

        onMounted(() => {
            updateRowData(props.data);
            if (tableContainerRef.value) {
                resizeObserver.observe(tableContainerRef.value);
            }
        });

        onUnmounted(() => {
            resizeObserver.disconnect();
        })

        return {
            rowData,
            colDefs,
            tableContainerRef,
            gridApi,
            updateRowData,
        };
    }
}
